import { useTranslation } from 'react-i18next'
import { Building2, Check, ChevronsUpDown, Plus } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import { useInstallations } from '@/features/installations/hooks/useInstallations'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { useSession } from '@/shared/hooks/useSession'
import { cn } from '@/shared/lib/utils'

export function InstallationWorkspaceSwitcher() {
  const { t } = useTranslation('common')
  const { installationId } = useParams<{ installationId: string }>()
  const { data: installations = [] } = useInstallations()
  const navigate = useNavigate()
  const { checkPermission } = usePermissions()
  const { session } = useSession()

  const canCreate = checkPermission('installation', 'create')
  const atCap = installations.length >= session.organization.maxNumberInstallations
  const current = installations.find(item => item.id === installationId)
  const newInstallationDisabled = !canCreate || atCap

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <Building2 className="size-4 shrink-0" />
              </div>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-semibold">{current?.name ?? '—'}</span>
              </div>
              <ChevronsUpDown className="ms-auto size-4 shrink-0" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-56 rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            {installations.map(installation => (
              <DropdownMenuItem
                key={installation.id}
                className="gap-2"
                onClick={() => navigate(`/dashboard/${installation.id}`)}
              >
                <Check
                  className={cn(
                    'size-4 shrink-0',
                    installation.id === installationId ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <span className="truncate">{installation.name}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={newInstallationDisabled}
              onClick={() => navigate('/installations/new')}
            >
              <Plus className="size-4 shrink-0" />
              {t('common:nav.newInstallation')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
